<template>
  <BaseCard
    v-bind="$attrs"
    :url-link="apartmentUrl(apartment)"
    :url-image="image"
    :badge="{text: translate(apartmentDisplayType(apartment)), color: apartmentDisplayColor(apartment)}"
    :hover-text="translate('Tutustu asuntoon')"
    :means-test="meansTest"
    :income-limit="incomeLimit"
    :own-plot="ownPlot"
    :housing-type="housingType"
    class="apartment-card"
  >
    <template v-slot:default="{title, line}">
      <div class="apartment-card__header">
        <div :class="line">
          {{apartmentDisplayLocation(apartment)}}
        </div>

        <h2 :class="title">
          {{apartment.mpc_name}}
        </h2>
      </div>
    </template>

    <!-- Seuraava esittely -->
    <template #afterImage>
      <div class="apartment-card__next-showing"
        v-if="apartment.showingInFuture">

        <BadgeAlertIcon />
        <span>
          {{sprintf(translate('Esittely %s'), apartmentDisplayShowing(apartment))}}
        </span>
      </div>
    </template>

    <!-- Basic specs -->
    <template #apartmentSpecs>
      <div class="base-card__specs base-card__specs--row">

        <HouseIcon />

        <span class="base-card__spec">
          {{apartment.mpc_huoneistotyyppi}}
        </span>
        <span class="base-card__separator">|</span>

        <span class="base-card__spec">
          {{apartmentDisplayArea(apartment)}}
        </span>
        <span class="base-card__separator">|</span>

        <span class="base-card__spec">
          {{ apartment.building.mpc_talotyyppi ? translate(apartment.building.mpc_talotyyppi) : ''}}
        </span>
      </div>
    </template>

    <template #footer>

      <!-- Action buttons -->
      <div class="apartment-card__footer-attribute">
        <div v-if="isReserved">
          <div class="apartment-card__footer-attribute-title">&nbsp;</div>
          <div class="apartment-card__footer-attribute-value">{{translate('Varattu')}}</div>
        </div>
        <div v-else>
          <div class="apartment-card__footer-attribute-title">{{translate('Vapaa')}}</div>
          <div class="apartment-card__footer-attribute-value">{{apartmentDisplayAvailable(apartment)}}</div>
        </div>
      </div>

      <div
          v-if="apartment.type === 'owner-occupancy'"
          class="apartment-card__footer-attribute">
            <div class="apartment-card__footer-attribute-title">{{ translate('Myyntihinta') }}</div>
            <div class="apartment-card__footer-attribute-value">{{ apartmentDisplaySellPrice(apartment) }}</div>
        </div>

      <div
        v-if="apartment.type === 'owner-occupancy'"
        class="apartment-card__footer-attribute">
          <div class="apartment-card__footer-attribute-title">{{ translate(apartmentDisplayPriceTitle(apartment)) }}</div>
          <div class="apartment-card__footer-attribute-value">{{ apartmentDisplayPrice(apartment) }}</div>
      </div>
      <div
        v-else
        class="apartment-card__footer-attribute">
        <div class="apartment-card__footer-attribute-title">{{translate(apartmentDisplayPriceTitle(apartment))}}</div>
        <div class="apartment-card__footer-attribute-value">{{apartmentDisplayPrice(apartment)}}</div>
      </div>

      <div
        v-if="apartment.type === 'right-of-occupancy'"
        class="apartment-card__footer-attribute"
      >
        <div class="apartment-card__footer-attribute-title">{{translate('ASO-maksu')}}</div>
        <div class="apartment-card__footer-attribute-value">
          {{`${formatStringDecimal(apartment.mpc_asomaksuyhteensa)} €`}}
        </div>
      </div>
    </template>

    <template #afterLink>
      <div class="apartment-card__favourite" :test="apartment.mpc_status">
        <FavouriteToggle
          :uuid="apartment.mpc_asuntoid"
          :type="apartment.type ? apartment.type : 'right-of-occupancy'"
          :status="apartment.mpc_status"
        />
      </div>
    </template>

  </BaseCard>
</template>

<script>
  import {sprintf} from 'sprintf-js';

  import {formatStringDecimal, formatStringInt} from '../../lib/format';
  import utils from '../mixins/utils';
  import apartmentDisplayFields from '../mixins/apartmentDisplayFields';
  import FavouriteToggle from '../FavouriteToggle';
  import BaseCard from './BaseCard';
  import BadgeAlertIcon from '../icons/BadgeAlertIcon.vue';
  import HouseIcon from '../icons/HouseIcon.vue';

  export default {
    components: {
      FavouriteToggle,
      BaseCard,
      BadgeAlertIcon,
      HouseIcon,
    },
    mixins: [
      apartmentDisplayFields,
      utils.translate,
      utils.apartmentUrl,
    ],
    props: {
      apartment: {
        type: Object,
        required: true,
      },
    },
    computed: {
      image() {
        if(this.apartment.imagesForWeb.length <= 0 ) {
          return null;
        }
        for(let i = 0; i < this.apartment.imagesForWeb.length; i++) {
          if(this.apartment.imagesForWeb[i].type !== 'Myyntiasiakirja') {
            const mainPicture = this.apartment.imagesForWeb[i];
            return mainPicture ? mainPicture.storage_thumbnail : null;
          }
        }
      },
      isReserved() {
        return this.apartment.mpc_status === 'Varattu';
      },
      incomeLimit() {
        if (this.apartment.type != 'owner-occupancy') {
          return this.apartment.housing_locality.new_tulorajat;
        } else {
          return false;
        }
      },
      meansTest() {
        return this.apartment.housing_locality.ava_tarveharkinta;
      },
      ownPlot() {
        return this.apartment.housing_locality.ava_tontinomistus === "Oma" ? true : false;
      },
      housingType() {
        return this.apartment.type;
      },
    },
    methods: {
      sprintf,
      formatStringDecimal,
    }
  }
</script>

<style lang="scss">
  @import "~styles/definitions";

  .apartment-card {
    display: flex;
    flex-flow: column;
    width: 100%;
  }

  .apartment-card__header {
    color: $black;
  }

  .apartment-card__footer-attribute {
    margin-right: r(20);

    &:last-child {
      margin-right: 0;
    }
  }

  .apartment-card__footer-attribute-title {
    font-size: r(12);
    text-transform: uppercase;
    line-height: r(16);
  }

  .apartment-card__footer-attribute-value {
    font-size: 1rem;
    font-weight: $font-weight-bold;
  }

  .apartment-card__next-showing {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: .75rem 1rem;
    background-color: $color-pink-2025;
    font-weight: $font-weight-bold;
    font-size: 1rem;
    color: $color-green-2025;

    .icon {
      margin-right: .75rem;
    }
  }

  .apartment-card__favourite {
    width: 100%;
  }
</style>
